// Stage pointer input: click hit-testing, hover priority, camera drag, and
// bed dragging. Extracted from GameScreen to respect the line limit.
// Beds drag only when free (nobody crashed); movement steering reads live
// bed positions, so NPCs reroute within a retarget or two.
import { useRef } from 'react';
import { TUNING } from '../constants.js';
import { findBedAt } from '../sim/engine.js';
import { hoverPriority } from '../sim/bubbles.js';
import { canvasDimsFor, ROOM_PAD_TOP, ROOM_PAD_X } from './draw.js';
import type { ActiveConvo } from './useConversations.js';
import type { WorldState } from './useSim.js';
import type { BubbleRect } from './worldRenderer.js';

export interface PointerDeps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  worldRef: React.MutableRefObject<WorldState | null>;
  activeRef: React.MutableRefObject<ActiveConvo[]>;
  bubbleRectsRef: React.MutableRefObject<BubbleRect[]>;
  hoverConvoRef: React.MutableRefObject<string | null>;
  lastClient: React.MutableRefObject<{ x: number; y: number }>;
  camera: {
    beginDrag: (sx: number, sy: number) => void;
    dragTo: (sx: number, sy: number) => void;
    endDrag: () => boolean;
    flyTo: (
      stageW: number,
      stageH: number,
      worldX: number,
      worldY: number,
      canvasW: number,
      canvasH: number,
      worldPadX?: number,
      worldPadTop?: number,
    ) => void;
    cancelFly: () => void;
  };
  setWorld: (w: WorldState) => void;
  skipBubble: (id: string) => void;
  bioId: string | null;
  setBioId: (id: string | null) => void;
  setRadial: (r: { id: string; x: number; y: number } | null) => void;
  centerOnSelect?: boolean;
}

export function useStagePointer(d: PointerDeps): {
  onStageMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  onStageMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  onStageMouseUp: (e: React.MouseEvent<HTMLDivElement>) => void;
  onStageMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => void;
} {
  const dragBed = useRef<{ id: string } | null>(null);
  const deps = useRef(d);
  deps.current = d;

  // Aquarium room is the canvas itself. The legacy square still has a pad.
  function roomPad(): { x: number; y: number } {
    const w = deps.current.worldRef.current;
    if (w?.width != null && w.height != null) return { x: 0, y: 0 };
    return { x: ROOM_PAD_X, y: ROOM_PAD_TOP };
  }

  // Screen px → world coords (zoom/pan transform + room offset).
  function toWorld(clientX: number, clientY: number): { cx: number; cy: number } | null {
    const canvas = deps.current.canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const pad = roomPad();
    return {
      cx: ((clientX - rect.left) / rect.width) * canvas.width - pad.x,
      cy: ((clientY - rect.top) / rect.height) * canvas.height - pad.y,
    };
  }

  // Hover pick: bubble rects first (they render above bodies), then NPC
  // bodies + nameplates. Returns npc and/or bubble convo membership.
  function hoverPick(cx: number, cy: number): { npcId: string | null; bubbleId: string | null } {
    const w = deps.current.worldRef.current;
    let bubbleId: string | null = null;
    for (const r of deps.current.bubbleRectsRef.current) {
      if (cx >= r.x0 && cx <= r.x1 && cy >= r.y0 && cy <= r.y1) {
        bubbleId = r.id;
        break;
      }
    }
    let npcId: string | null = null;
    if (w) {
      for (const r of w.runtime) {
        if (Math.abs(r.x - cx) < 20 && cy > r.y - 26 && cy < r.y + 42) {
          npcId = r.npcId;
          break;
        }
      }
    }
    return { npcId, bubbleId };
  }

  // Hit-test in world coords: bubbles first (click-to-advance), then NPCs.
  function hitTest(cx: number, cy: number): void {
    const dd = deps.current;
    const w = dd.worldRef.current;
    if (!w) return;
    for (const r of dd.bubbleRectsRef.current) {
      if (cx >= r.x0 - 8 && cx <= r.x1 + 8 && cy >= r.y0 - 8 && cy <= r.y1 + 8) {
        dd.skipBubble(r.id);
        return;
      }
    }
    let best: string | null = null;
    let bestD = 26;
    for (const r of w.runtime) {
      const dist = Math.hypot(r.x - cx, r.y - cy);
      if (dist < bestD) {
        bestD = dist;
        best = r.npcId;
      }
    }
    if (best) {
      if (dd.centerOnSelect === true) {
        const canvas = dd.canvasRef.current;
        const stage = document.querySelector('.stage') as HTMLDivElement | null;
        const target = w.runtime.find((r) => r.npcId === best);
        if (canvas && stage && target) {
          const pad = roomPad();
          const dims = pad.x === 0 ? { w: canvas.width, h: canvas.height } : canvasDimsFor(w.size);
          dd.camera.flyTo(stage.clientWidth, stage.clientHeight, target.x, target.y, dims.w, dims.h, pad.x, pad.y);
        }
      }
      if (dd.bioId) {
        // Bio already open: swap straight to this person (same one toggles shut).
        dd.setBioId(best === dd.bioId ? null : best);
        dd.setRadial(null);
      } else {
        dd.setRadial({ id: best, x: dd.lastClient.current.x, y: dd.lastClient.current.y });
        dd.setBioId(null);
      }
    } else {
      dd.setRadial(null);
    }
  }

  function bedAt(cx: number, cy: number): { id: string } | null {
    const w = deps.current.worldRef.current;
    if (!w) return null;
    const bed = findBedAt(w.beds, cx, cy);
    if (!bed) return null;
    // Occupied beds are bolted down while someone sleeps in them.
    const occupied = w.runtime.some((r) => r.restingBed === bed.id);
    return occupied ? null : { id: bed.id };
  }

  function onStageMouseDown(e: React.MouseEvent<HTMLDivElement>): void {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('.bio,.radial-disc,button')) return; // UI keeps its clicks
    const sr = e.currentTarget.getBoundingClientRect();
    deps.current.lastClient.current = { x: e.clientX - sr.left, y: e.clientY - sr.top };
    const pt = toWorld(e.clientX, e.clientY);
    const grab = pt && bedAt(pt.cx, pt.cy);
    if (grab) {
      dragBed.current = grab; // bed drag suppresses camera pan + clicks
      return;
    }
    deps.current.camera.beginDrag(e.clientX, e.clientY);
  }

  function setCursor(v: string): void {
    const canvas = deps.current.canvasRef.current;
    if (canvas) canvas.style.cursor = v;
  }

  function onStageMouseMove(e: React.MouseEvent<HTMLDivElement>): void {
    if (dragBed.current) {
      // Drag the bed with the cursor, clamped inside the walls.
      const w = deps.current.worldRef.current;
      const pt = toWorld(e.clientX, e.clientY);
      if (w && pt) {
        const m = TUNING.bedMarginPx;
        const bx = Math.min(w.size - m, Math.max(m, pt.cx));
        const by = Math.min(w.size - m, Math.max(m, pt.cy));
        const id = dragBed.current.id;
        deps.current.setWorld({ ...w, beds: w.beds.map((b) => (b.id === id ? { ...b, x: bx, y: by } : b)) });
      }
      setCursor('grabbing');
      return;
    }
    deps.current.camera.dragTo(e.clientX, e.clientY);
    // Hover priority: track what's under the cursor so its convo draws on top.
    if ((e.target as HTMLElement).closest('.bio,.radial-disc,button')) {
      deps.current.hoverConvoRef.current = null;
      return;
    }
    const pt = toWorld(e.clientX, e.clientY);
    if (!pt) return;
    const { npcId, bubbleId } = hoverPick(pt.cx, pt.cy);
    deps.current.hoverConvoRef.current = hoverPriority(npcId, bubbleId, deps.current.activeRef.current);
    if (!bubbleId && !npcId) {
      // Free bed under cursor → grabbable.
      const w = deps.current.worldRef.current;
      setCursor(w && bedAt(pt.cx, pt.cy) ? 'grab' : '');
    } else {
      setCursor('pointer');
    }
  }

  function onStageMouseUp(e: React.MouseEvent<HTMLDivElement>): void {
    if (dragBed.current) {
      dragBed.current = null; // bed drop: plain release, no click action
      setCursor('');
      deps.current.camera.endDrag(); // balance (never began, returns false)
      return;
    }
    const wasClick = deps.current.camera.endDrag();
    const sr = e.currentTarget.getBoundingClientRect();
    deps.current.lastClient.current = { x: e.clientX - sr.left, y: e.clientY - sr.top };
    if (!wasClick) return;
    if ((e.target as HTMLElement).closest('.bio,.radial-disc,button')) return;
    const pt = toWorld(e.clientX, e.clientY);
    if (!pt) return;
    hitTest(pt.cx, pt.cy);
  }

  function onStageMouseLeave(e: React.MouseEvent<HTMLDivElement>): void {
    deps.current.hoverConvoRef.current = null;
    setCursor('');
    onStageMouseUp(e);
  }

  return { onStageMouseDown, onStageMouseMove, onStageMouseUp, onStageMouseLeave };
}
